/**
 * Creates a cropped image from the given image source and crop area
 * @param {string} imageSrc - The source URL of the image
 * @param {Object} cropArea - The crop area with x, y, width, height
 * @param {number} rotation - Rotation angle in degrees
 * @returns {Promise<File>} - Promise that resolves to the cropped image file
 */
export const createCroppedImage = (imageSrc, cropArea, rotation = 0) => {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      try {
        // Step 1: Create a canvas for the rotated image (if needed)
        let sourceCanvas = document.createElement('canvas');
        let sourceCtx = sourceCanvas.getContext('2d');
        let sourceImage = image;

        if (rotation !== 0) {
          // Calculate dimensions for rotated image
          const rotationRad = (rotation * Math.PI) / 180;
          const cos = Math.abs(Math.cos(rotationRad));
          const sin = Math.abs(Math.sin(rotationRad));
          const rotatedWidth = image.width * cos + image.height * sin;
          const rotatedHeight = image.width * sin + image.height * cos;

          sourceCanvas.width = rotatedWidth;
          sourceCanvas.height = rotatedHeight;

          // Clear with white background
          sourceCtx.fillStyle = '#FFFFFF';
          sourceCtx.fillRect(0, 0, rotatedWidth, rotatedHeight);

          // Rotate and draw the original image
          sourceCtx.save();
          sourceCtx.translate(rotatedWidth / 2, rotatedHeight / 2);
          sourceCtx.rotate(rotationRad);
          sourceCtx.drawImage(image, -image.width / 2, -image.height / 2);
          sourceCtx.restore();

          // Create a new image from the rotated canvas
          sourceImage = sourceCanvas;
        }

        // Step 2: Create the final crop canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Set canvas size to the crop area
        canvas.width = cropArea.width;
        canvas.height = cropArea.height;

        // Clear the canvas with white background
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw the cropped portion from the source (rotated if needed)
        ctx.drawImage(
          sourceImage,
          cropArea.x, // source x
          cropArea.y, // source y
          cropArea.width, // source width
          cropArea.height, // source height
          0, // dest x
          0, // dest y
          cropArea.width, // dest width
          cropArea.height // dest height
        );

        // Try to convert canvas to blob
        try {
          canvas.toBlob(
            blob => {
              if (!blob) {
                reject(new Error('Failed to create cropped image'));
                return;
              }

              // Create a file from the blob
              const file = new File([blob], 'cropped-image.jpg', {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });

              resolve(file);
            },
            'image/jpeg',
            0.9 // Quality
          );
        } catch (blobError) {
          console.error('toBlob error:', blobError);
          // Fallback: try using getImageData and manual blob creation
          try {
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const fallbackCanvas = document.createElement('canvas');
            fallbackCanvas.width = canvas.width;
            fallbackCanvas.height = canvas.height;
            const fallbackCtx = fallbackCanvas.getContext('2d');
            fallbackCtx.putImageData(imageData, 0, 0);

            fallbackCanvas.toBlob(
              blob => {
                if (!blob) {
                  reject(new Error('Failed to create cropped image with fallback'));
                  return;
                }

                const file = new File([blob], 'cropped-image.jpg', {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });

                resolve(file);
              },
              'image/jpeg',
              0.9
            );
          } catch (fallbackError) {
            console.error('Fallback error:', fallbackError);
            reject(new Error('All canvas export methods failed'));
          }
        }
      } catch (error) {
        console.error('Canvas drawing error:', error);
        reject(error);
      }
    };

    image.onerror = error => {
      console.error('Image load error:', error);
      reject(new Error('Failed to load image'));
    };

    // Load the image - don't set crossOrigin for data URLs
    if (imageSrc.startsWith('data:')) {
      image.src = imageSrc;
    } else {
      image.crossOrigin = 'anonymous';
      image.src = imageSrc;
    }
  });
};

/**
 * Creates a preview URL for the cropped image
 * @param {string} imageSrc - The source URL of the image
 * @param {Object} cropArea - The crop area with x, y, width, height
 * @param {number} rotation - Rotation angle in degrees
 * @returns {Promise<string>} - Promise that resolves to the preview URL
 */
export const createCroppedImageUrl = async (imageSrc, cropArea, rotation = 0) => {
  try {
    const croppedFile = await createCroppedImage(imageSrc, cropArea, rotation);
    return URL.createObjectURL(croppedFile);
  } catch (error) {
    console.error('Error creating cropped image URL:', error);
    throw error;
  }
};
